<template>
  <v-form
    ref="vForm"
    :value="value"
    :lazy-validation="lazyValidation"
    v-bind="$attrs"
    @submit="submit($event)"
    @input="$emit('input', $event)"
  >
    <slot></slot>
  </v-form>
</template>

<script lang="ts">
import { getScrollParent } from "@/app/ui/utilities";
import {
  defineComponent,
  getCurrentInstance,
  inject,
  onUnmounted,
  provide,
  ref,
} from "vue";
import { useVuetify } from "@/plugins/vuetify";
import type { Validator } from "@/components/platform/hybrid/forms";
import {
  registerValidatorKey,
  unregisterValidatorKey,
} from "@/components/platform/hybrid/forms";
import Guid from "@/app/types/common/guid";

/**
 * Keeps track of all controls in a form and scrolls to the first validation error on validate.
 * Use this as a replacement for v-form, not in conjunction.
 */
export default defineComponent({
  name: "BmForm",
  props: {
    value: { type: Boolean },
    lazyValidation: { type: Boolean, default: false, required: false },
    preventSubmit: { type: Boolean, default: false, required: false },
  },
  setup(props, context) {
    const formId = Guid.create();
    const vForm = ref<any>(null);
    const { $vuetify } = useVuetify();

    const instance = getCurrentInstance();
    provide("form", instance);

    const registerValidator = inject(registerValidatorKey, undefined);
    registerValidator?.(formId, instance?.proxy as unknown as Validator);
    const unregisterValidator = inject(unregisterValidatorKey, undefined);

    /** public */
    const focus = () => {
      // Breaks v-form encapsulation
      if (
        vForm.value.inputs.length > 0 &&
        typeof vForm.value.inputs[0].focus === "function"
      ) {
        // Seem to need to allow the browser to re-calculate focusable elements prior to changing focus
        setTimeout(() => vForm.value.inputs[0].focus(), 10);
      }
    };

    const validate = (): boolean => {
      // Breaks encapsulation and accesses the inner details of VForm. Likely to break if VForm implementation changes.
      const errorInputs = vForm.value.inputs.filter(
        (input: any) => !input.validate(true)
      );

      // scrolls to first failing element
      if (errorInputs.length) {
        // This doesn't seem to work with child
        $vuetify
          .goTo(errorInputs[0], {
            offset: 100,
            container: getScrollParent(errorInputs[0].$el, false),
          })
          .then();
      }

      return !errorInputs.length;
    };

    const reset = () => {
      vForm.value.reset();
    };

    const resetValidation = () => {
      vForm.value.resetValidation();
    };

    const submit = (event: Event) => {
      if (props.preventSubmit) {
        event.preventDefault();
      } else {
        context.emit("submit", event);
      }
    };

    onUnmounted(() => {
      unregisterValidator?.(formId);
    });

    return {
      vForm,
      submit,

      // Public API
      focus,
      validate,
      reset,
      resetValidation,
    };
  },
});
</script>
